<template>
  <button
    type="button"
    class="quantity-select-placeholder"
    aria-haspopup="listbox"
    aria-expanded="false"
    @mousedown="emit('intent', $event)"
    @touchstart.passive="emit('intent', $event)"
    @keydown.enter.space.down.up.prevent="emit('intent', $event)"
  >
    <span class="quantity-select-placeholder__label">{{ label }}</span>
    <SvgWrapper
      class="quantity-select-placeholder__icon"
      use="icon_svg_down"
      dimensions="24"
    />
  </button>
</template>

<script setup lang="ts">
/**
 * Stand-in for QuantitySelect while its chunk has not been loaded (VIP-12356):
 * the closed outlined field with the current quantity and the menu chevron,
 * with the same footprint as Vuetify's, so the swap moves nothing. It is not
 * a select — a pointer down, touch or key on it reports an `intent`, Cta
 * loads the real component and replays the gesture on it.
 */
import SvgWrapper from '@/components/basic/atoms/SvgWrapper.vue'

defineProps<{ label: string }>()
const emit = defineEmits<{ intent: [event: Event] }>()
</script>

<style scoped>
/* Measured against the rendered VSelect (56px, outlined, as configured in
   plugins/vuetify.js): 16px text inset, 12px between text and the 24px
   chevron, 12px after it; the text on a 24px line so the baseline — which
   positions the inline-flex CTA — lands where the field's does. The outline
   is a pseudo-element like Vuetify's, so it takes no layout space, and it
   mirrors Cta's override (no right border, square right corners). */
.quantity-select-placeholder {
  /* a button, so focus and keyboard come for free — minus the button look */
  appearance: none;
  margin: 0;
  border: 0;
  background: none;
  font: inherit;
  text-align: left;
  position: relative;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  height: 56px;
  min-width: 75px;
  padding: 0 12px 0 16px;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: 1rem;
  letter-spacing: 0.009375em;
  line-height: 1.5;
  cursor: pointer;
  user-select: none;
}

/* Baseline anchor. The CTA is inline-flex and takes its baseline from its
   first child. VSelect's root is a grid that exports none, so the CTA's
   baseline is synthesised at the select's bottom edge and the line box gains
   the strut's descender below it (measured: 8.5px on the product stage). A
   label with text would give the CTA a real baseline instead, and everything
   under the CTA would jump by those 8.5px when the select takes over. This
   empty, zero-width first child has no baseline either, so the CTA behaves
   identically in both states. */
.quantity-select-placeholder::before {
  content: '';
  display: block;
  width: 0;
  align-self: stretch;
}

.quantity-select-placeholder::after {
  content: '';
  position: absolute;
  inset: 0;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.38);
  border-right: 0;
  border-radius: 4px 0 0 4px;
  pointer-events: none;
}

.quantity-select-placeholder:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.quantity-select-placeholder__label {
  flex: 1 1 auto;
  white-space: nowrap;
}

.quantity-select-placeholder__icon {
  flex: 0 0 auto;
  margin-left: 12px;
  opacity: var(--v-medium-emphasis-opacity);
}
</style>
